import { useEffect, useRef, useState, type FormEvent } from "react";

export interface AliasModalProps {
  open: boolean;
  initialAlias: string;
  saving?: boolean;
  onClose: () => void;
  onSave: (newAlias: string) => void | Promise<void>;
}

export function AliasModal({ open, initialAlias, saving = false, onClose, onSave }: AliasModalProps) {
  const [value, setValue] = useState(initialAlias);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setValue(initialAlias);
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open, initialAlias]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, saving]);

  if (!open) return null;

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || saving) return;
    void onSave(trimmed);
  }

  return (
    <div className="modal-backdrop" data-testid="alias-modal" onClick={saving ? undefined : onClose}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>Change display name</h2>
        <input
          ref={inputRef}
          data-testid="alias-modal-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={50}
          placeholder="Your name or alias"
          disabled={saving}
        />
        <div className="modal-actions">
          <button type="button" className="btn ghost sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn primary sm" disabled={!value.trim() || saving} data-testid="alias-modal-save">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
